// /t/<storage path>?k=<token> — one library track for a family, reached from
// an email. The email links this page on coloryourvoice.com instead of the
// Storage host (see src/services/trackLinks.js for why); the page rebuilds the
// Storage URL from the path and token in its own address, plays it in a
// native <audio> (no sign-in, no Google session: the Storage copy is the one
// host every surface plays from, systems.md → Music library → Playback), and
// offers the same open-in-a-tab download the family Songs page has.
import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToLine, faMusic } from '@fortawesome/pro-solid-svg-icons';
import { describeTrackFile, parseTrackLink, storageUrlFor } from '../../services/trackLinks';
import '../Register/RegisterStudent.css';
import './TrackLink.css';

const bucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || undefined;

export default function TrackLink() {
	const params = useParams();
	const [search] = useSearchParams();
	const link = parseTrackLink(params['*'], search.toString());

	const shell = (title, body) => (
		<main className='register-page cyv track-link'>
			<Helmet defer={false}>
				<title>{title} | Color Your Voice</title>
			</Helmet>
			<div className='register-card'>
				<div className='register-inner'>{body}</div>
			</div>
		</main>
	);

	if (!link) {
		return shell(
			'Link not valid',
			<div className='register-success'>
				<h1 className='register-success__title'>This link isn't valid.</h1>
				<p className='register-success__body'>
					It may have been copied incompletely. Reply to the email we sent you and we'll send a fresh
					one.
				</p>
			</div>
		);
	}

	const url = storageUrlFor({ path: link.path, token: link.token, ...(bucket ? { bucket } : {}) });
	const { title, detail } = describeTrackFile(link.path);

	return shell(
		title,
		<>
			<header className='rg-head'>
				<span className='eyebrow'>
					<FontAwesomeIcon icon={faMusic} className='track-link__eyebrow-i' aria-hidden='true' />
					Your track
				</span>
				<h1 className='rg-title'>{title}</h1>
				{detail && <p className='rg-sub'>{detail}</p>}
			</header>
			{/* A native control: it plays on every phone without a tap-through,
			    and this page has no key change or lyrics to drive. */}
			<audio className='track-link__audio' controls preload='metadata' src={url} data-testid='track-audio'>
				Your browser can't play this here. Use the download link below.
			</audio>
			<div className='track-link__actions'>
				<a
					href={url}
					target='_blank'
					rel='noopener noreferrer'
					className='btn-cyv-teal track-link__download'
					title='Open the file in a new tab to save it'>
					<FontAwesomeIcon icon={faArrowDownToLine} aria-hidden='true' />
					Download
				</a>
				<p className='track-link__hint'>
					Opens the file in a new tab. On a phone, use Share or the download icon there to save it.
				</p>
			</div>
			<p className='track-link__portal'>
				This track also lives on the Songs page in your{' '}
				<Link to='/portal/login'>family portal</Link>. Having trouble? Reply to the email it came in.
			</p>
		</>
	);
}
